import React, {
  ComponentType,
  useState
} from 'react'
import { Accordion } from 'semantic-ui-react'
// Local
import { CONDITION } from '../../../constant'
import PhysicianCondition from './physicianCondition'
import PhysicianConditionHccHeaderTitle from './physicianConditionHccHeaderTitle'
import {
  EncounterConditionDisplay
} from '../../authenticated/types'
import { ObjectValues } from '../../../types/types'

const SafeAccordion = Accordion as unknown as ComponentType<any>

type PhysicianConditionHccGroupProps = {
  mode?: string | null
  hccCode: string | null
  hccDescription: string | null
  activeConditionId: string | null
  conditionGroupTitle: string
  conditions: EncounterConditionDisplay[]
  onConditionAction: (
    action: ObjectValues<typeof CONDITION.ACTION>,
    condition: EncounterConditionDisplay | string | null
  ) => void
}

/**
 * Provides a component that displays conditions grouped under an HCC for the
 * physician user role
 *
 * @param mode View mode. Options: "smart", null. Default: null
 * @param hccCode HCC code
 * @param hccDescription HCC description
 * @param activeConditionId currently set active conditionId from the last call of
 *                          "this.props.onConditionAction(ACTIVATE || DEACTIVATE, this.domKey)"
 *                          this value is set by: `${this.props.conditionGroupTitle}-${this.props.hccCode}`
 * @param conditionGroupTitle the conditionStatus group, values: KNOWN, POTENTIAL, or ACTIVITY
 * @param conditions List of conditions to display in the group
 * @param onConditionAction Callback for condition action.
 *        Arguments: action, condition
 */
function PhysicianConditionHccGroup(
  props: PhysicianConditionHccGroupProps
): JSX.Element | null {
  const {
    mode: modeProp,
    hccCode,
    hccDescription,
    activeConditionId: activeConditionIdProp,
    conditionGroupTitle,
    conditions: conditionsProp,
    onConditionAction
  } = props

  const domKey = `${conditionGroupTitle}-${hccCode}`
  const [ isActive, setIsActive ] = useState<boolean>(
    activeConditionIdProp === domKey
  )

  const handleClickTitle = () => {
    const { ACTIVATE, DEACTIVATE, READ } = CONDITION.ACTION

    if (isActive === true) {
      setIsActive(false)
      onConditionAction(DEACTIVATE, domKey)
    } else {
      setIsActive(true)
      onConditionAction(ACTIVATE, domKey)
      conditionsProp.forEach(c => {
        onConditionAction(READ, c)
      })
    }
  }

  const hccGroupTitle = (conditions: EncounterConditionDisplay[]): JSX.Element => (
    <PhysicianConditionHccHeaderTitle
      conditions={ conditions }
      active={ isActive }
      mode={ modeProp === 'smart' ? 'smart' : null }
      hccCode={ hccCode || '' }
      hccDescription={ hccDescription || '' }
      onConditionAction={ onConditionAction as unknown as (action: any, condition: EncounterConditionDisplay) => void }
    />
  )

  const renderConditions = (
    conditions: EncounterConditionDisplay[]
  ): JSX.Element => (
    <div>
      <Accordion.Title
        key={ domKey }
        active={ isActive }
        onClick={ handleClickTitle }
      >
        <div>{ hccGroupTitle(conditions) }</div>
      </Accordion.Title>
      <Accordion.Content active={ isActive }>
        <SafeAccordion fluid>
          { conditionsProp.map((condition, displayIndex) => (
            <PhysicianCondition
              key={ condition.id }
              mode={ modeProp }
              active={ isActive }
              hccChild={ true }
              condition={ condition }
              displayIndex={ displayIndex }
              onConditionAction={ onConditionAction }
            />
          )) }
        </SafeAccordion>
      </Accordion.Content>
    </div>
  )

  return conditionsProp && conditionsProp.length > 0
    ? renderConditions(conditionsProp)
    : null
}

export default PhysicianConditionHccGroup
